/* global removeTrapFocus */

const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    const inline = this.classList.contains('magpie-inline-search');
    if (inline) {
      this.addEventListener('focusin', () => { this.classList.add('magpie-search-open'); this.querySelector('.main-search__input').setAttribute('aria-expanded', String(!!this.querySelector('.main-search__results').children.length)); });
      // Touch keyboards can report no relatedTarget when a suggestion is tapped.
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.dismissSearch();
      });
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.dismissSearch();
      });
    }
    const input = this.querySelector('.main-search__input');
    const form = this.querySelector('form');
    input.setAttribute('enterkeyhint', 'search');
    input.setAttribute('autocapitalize', 'none');
    input.setAttribute('spellcheck', 'false');
    // Keep native GET submission independent of predictive requests.
    form.addEventListener('submit', (event) => {
      if (!input.value.trim()) { event.preventDefault(); return; }
      input.blur();
      this.classList.remove('magpie-search-open');
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('.main-search__results a')) input.blur();
    });
    if (window.matchMedia('(max-width: 767px)').matches) {
      input.removeAttribute('role');
      input.removeAttribute('aria-autocomplete');
      input.removeAttribute('aria-controls');
      input.removeAttribute('aria-activedescendant');
    }

    // Clicking close button
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.remove('show-search');
        if (inline) {
          this.classList.remove('magpie-search-open');
          this.querySelector('.main-search__input').setAttribute('aria-expanded', 'false');
          this.querySelector('.main-search__input').blur();
        } else { removeTrapFocus(theme.trapOpener); }
      });
    });

    // Pressing escape key
    this.querySelector('.main-search__input').addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape') {
        this.querySelector('.main-search__close').dispatchEvent(new Event('click'));
      }
    });

    // Search as you type
    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }

    // Result navigation
    this.activeResultIndex = -1;
    this.querySelector('.main-search__input').addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  dismissSearch() {
    this.classList.remove('magpie-search-open');
    this.querySelector('.main-search__input').setAttribute('aria-expanded', 'false');
    this.activeResultIndex = -1;
  }

  handleKeyDown(evt) {
    if (evt.isComposing) return;
    const options = [...this.querySelectorAll('.main-search__results [role="option"]')];

    if (!options.length) return;
    const updateActiveOption = () => {
      options.forEach((opt) => { opt.classList.remove('search-result-active'); opt.setAttribute('aria-selected', 'false'); });
      const active = options[this.activeResultIndex];
      active.classList.add('search-result-active');
      active.setAttribute('aria-selected', 'true');
      active.scrollIntoView({ block: 'nearest' });
      evt.currentTarget.setAttribute('aria-activedescendant', active.id);
    };

    if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      this.activeResultIndex = Math.min(this.activeResultIndex + 1, options.length - 1);
      updateActiveOption();
    }

    if (evt.key === 'ArrowUp') {
      evt.preventDefault();
      this.activeResultIndex = Math.max(this.activeResultIndex - 1, 0);
      updateActiveOption();
    }

    if (evt.key === 'Enter' && this.activeResultIndex >= 0) {
      evt.preventDefault();
      options[this.activeResultIndex]?.querySelector('a')?.click();
    }
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    const searchTimeoutThrottle = 250;
    const resultLimit = 10;
    const includeMeta = this.dataset.quickSearchMeta === 'true';
    let searchTimeoutID = -1;
    let searchAbortController = null;

    const handleInputChange = () => {
      const resultsBox = this.querySelector('.main-search__results');
      const valueToSearch = searchInput.value;

      // Mobile uses a plain native search form, then the full results page.
      // Avoid starting predictive requests or covering the page while typing.
      if (window.matchMedia('(max-width: 767px)').matches) {
        clearTimeout(searchTimeoutID);
        searchAbortController?.abort();
        searchAbortController = null;
        this.oldSearchValue = '';
        this.activeResultIndex = -1;
        resultsBox.replaceChildren();
        this.classList.remove('main-search--loading', 'main-search--has-results');
        searchInput.setAttribute('aria-expanded', 'false');
        return;
      }

      // Only search if search string longer than 2, and it has changed
      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        // Save previous value
        this.oldSearchValue = valueToSearch;

        // Kill outstanding ajax request
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }

        // Kill previous search
        clearTimeout(searchTimeoutID);

        // Create URL for full search results
        const form = searchInput.closest('form');
        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));
        formParams.forEach((value, key) => { linkURL.searchParams.set(key, value); });

        // Show loading
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
        this.classList.add('main-search--loading');
        if (!this.querySelector('.main-search__results-spinner')) {
          resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
        }

        // Do next search (in X milliseconds)
        searchTimeoutID = setTimeout(() => {
          searchAbortController = new AbortController();
          let ajaxUrl;

          if (theme.Shopify.features.predictiveSearch) {
            // use the API
            ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
            ajaxUrl.searchParams.set('q', valueToSearch);
            ajaxUrl.searchParams.set('section_id', 'predictive-search');
            ajaxUrl.searchParams.set('resources[limit]', resultLimit);
            ajaxUrl.searchParams.set('resources[limit_scope]', 'each');
            ajaxUrl.searchParams.set('resources[type]', 'product,collection,query,page,article');
            ajaxUrl.searchParams.set('resources[options][unavailable_products]', 'last');
            ajaxUrl.searchParams.set('resources[options][fields]', includeMeta ? 'title,product_type,variants.title,vendor,tag,variants.sku' : 'title,product_type,variants.title,vendor');
          } else {
            // use the theme template fallback
            ajaxUrl = new URL(linkURL);
            ajaxUrl.searchParams.set('section_id', 'main-search');
          }

          const titleURL = new URL(linkURL);
          const titleTokens = valueToSearch.match(/[\p{L}\p{N}]+/gu) || [];
          titleURL.searchParams.set('q', titleTokens.map((token) => 'title:' + token + '*').join(' AND '));
          titleURL.searchParams.set('type', 'product');
          titleURL.searchParams.set('section_id', 'magpie-title-search');
          const titleRequest = theme.Shopify.features.predictiveSearch && titleTokens.length
            ? fetch(titleURL, {signal: searchAbortController.signal})
              .then((response) => response.ok ? response.text() : '')
              .catch(() => '')
            : Promise.resolve('');
          fetch(ajaxUrl, { method: 'get', signal: searchAbortController.signal })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              }
              return response.text();
            })
            .then(async (response) => {
              const titleResponse = await titleRequest;
              const template = document.createElement('template');
              template.innerHTML = response;
              if (searchInput.value !== valueToSearch) return;
              const richResults = template.content.querySelector('.magpie-search-results');
              if (richResults) {
                searchAbortController = null;
                this.classList.remove('main-search--loading', 'main-search--no-results');
                this.classList.add('main-search--has-results');
                const productList = richResults.querySelector('.magpie-search-products');
                if (productList) {
                  const titleTemplate = document.createElement('template');
                  titleTemplate.innerHTML = titleResponse;
                  const candidates = [...titleTemplate.content.querySelectorAll('.magpie-search-product'), ...productList.querySelectorAll('.magpie-search-product')];
                  const seen = new Set();
                  const words = (text) => (text.toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) || []);
                  const terms = words(valueToSearch);
                  const wantsCards = terms.some((term) => ['card', 'cards'].includes(term));
                  const score = (item) => {
                    const name = item.querySelector('.magpie-search-product__copy > span')?.textContent || '';
                    const tokens = words(name);
                    const direct = terms.every((term) => tokens.some((token) => token === term || token === term + 's'));
                    const prefix = terms.every((term) => tokens.some((token) => token.startsWith(term)));
                    const card = !wantsCards && tokens.some((token) => ['card', 'cards'].includes(token));
                    return (direct ? 100 : prefix ? 50 : 0) - (card ? 150 : 0);
                  };
                  const unique = candidates.filter((item) => {
                    const href = item.querySelector('a').getAttribute('href').split('?')[0];
                    if (seen.has(href)) return false;
                    seen.add(href); return true;
                  });
                  unique.sort((a, b) => score(b) - score(a));
                  productList.replaceChildren(...unique.slice(0, resultLimit));
                }
                richResults.querySelectorAll('[data-search-all]').forEach((link) => {
                  link.href = linkURL;
                  link.textContent = 'See all results for “' + valueToSearch + '”';
                });
                richResults.querySelectorAll('[role="option"]').forEach((item, index) => {
                  item.id = (resultsBox.id || 'SearchResults') + '-suggestion-' + index;
                  item.setAttribute('aria-selected', 'false');
                });
                resultsBox.replaceChildren(richResults);
                const status = this.querySelector('[role="status"]');
                if (status) status.textContent = richResults.querySelector('[data-results-message]').textContent;
                this.activeResultIndex = -1;
                searchInput.removeAttribute('aria-activedescendant');
                searchInput.setAttribute('aria-expanded', 'true');
                return;
              }
              let resultsList = null;
              let resultsMessage = null;
              if (theme.Shopify.features.predictiveSearch) {
                resultsList = template.content.querySelector('.product-grid');
                resultsMessage = template.content.querySelector('[data-results-message]');
              } else {
                resultsList = template.content.querySelector('.section-search-template .product-grid');
                resultsMessage = template.content.querySelector('.section-search-template [data-results-message]');
              }

              // reset previous contents
              searchAbortController = null;
              this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
              resultsBox.innerHTML = '';

              const resultsProducts = document.createElement('div');
              resultsProducts.className = 'main-search__results__products collection-listing';
              resultsProducts.innerHTML = '<div></div>';
              const resultsPages = document.createElement('div');
              resultsPages.className = 'main-search__results__pages';

              resultsProducts.firstElementChild.className = resultsList.className;

              // Convert standard blocks into quick search result format
              resultsList.querySelectorAll('.product-block').forEach((block, index) => {
                block.setAttribute('role', 'option');
                block.id = `SearchResult${index}`;
              });
              resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)').forEach((block, index) => {
                if (index <= resultLimit) {
                  block.classList.add('main-search-result');
                  block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
                  block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
                  resultsProducts.firstElementChild.appendChild(block);
                }
              });

              resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
                const item = document.createElement('a');
                item.className = 'main-search-result main-search-result--page';
                item.href = block.querySelector('a').href;
                item.innerHTML = '<div class="main-search-result__text"></div>';
                item.firstElementChild.innerText = block.querySelector('.page-block__title').innerText;
                resultsPages.appendChild(item);
              });

              this.classList.remove('main-search--loading');

              const areProducts = !!resultsProducts.querySelector('.main-search-result');
              const arePages = !!resultsPages.querySelector('.main-search-result');
              if (areProducts || arePages) {
                const status = document.querySelector('#HeaderSearchResultsStatus');
                if (status) status.innerHTML = resultsMessage.innerHTML;
                // Numerous results
                this.classList.add('main-search--has-results');
                this.classList.toggle('main-search--results-on-multiple-lines', resultsProducts.querySelectorAll('.product-block').length > 4);
                if (areProducts) {
                  resultsBox.appendChild(resultsProducts);
                }
                if (arePages) {
                  const heading = document.createElement('h6');
                  heading.className = 'main-search-result__heading';
                  heading.innerHTML = theme.strings.generalSearchPages;
                  resultsPages.insertAdjacentElement('afterbegin', heading);
                  resultsBox.appendChild(resultsPages);
                }

                const allLink = document.createElement('a');
                allLink.className = 'main-search__results-all-link btn btn--secondary';
                allLink.href = linkURL;
                allLink.innerHTML = theme.strings.generalSearchViewAll;
                resultsBox.appendChild(allLink);
                searchInput.setAttribute('aria-expanded', true);
                this.activeResultIndex = -1;
              } else {
                // No results - show nothing
                this.classList.add('main-search--no-results');
                const emptyMessage = document.createElement('div');
                emptyMessage.className = 'main-search__empty-message';
                emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                resultsBox.appendChild(emptyMessage);
                searchInput.setAttribute('aria-expanded', false);
              }
            })
            .catch((error) => {
              console.warn(error); // eslint-disable-line no-console
            });
        }, searchTimeoutThrottle);
      } else if (!valueToSearch.length) {
        // Abandon current search
        this.oldSearchValue = valueToSearch;
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);

        // Clear results
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--loading');
        resultsBox.innerHTML = '';
        searchInput.setAttribute('aria-expanded', false);
      }
    };

    searchInput.addEventListener('input', handleInputChange.bind(this));
    searchInput.addEventListener('change', handleInputChange.bind(this));
  }
};

window.customElements.define('main-search', MainSearch);
